import { formatShiftTime } from "@/helpers/allocations";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ALLOCATION_SKILL_TYPES, ALLOCATION_SKILLS, SKILL_IDS } from "@/constants/allocations";
import { AllocationSkillId, AllocationSkills, AvailableSkill, WorkerAllocation } from "@/types/allocations";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Building2, Check, Clock } from "lucide-react";
import { cn } from "@/lib/utils";
import "./EditAgentModal.css";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";

export interface EditAgentPayload {
  allocationSkills?: AllocationSkills;
  skillIds?: number[];
  shiftStart?: string;
  shiftEnd?: string;
}

interface EditAgentModalProps {
  open: boolean;
  onClose: () => void;
  worker: WorkerAllocation;
  availableSkills: AvailableSkill[];
  userSkillIds: number[];
  isSaving: boolean;
  onSave: (userId: number, payload: EditAgentPayload) => Promise<void>;
}

export const EditAgentModal = ({
  open,
  onClose,
  worker,
  availableSkills,
  userSkillIds,
  isSaving,
  onSave,
}: EditAgentModalProps) => {
  const [localCaps, setLocalCaps] = useState<AllocationSkills>({
    ...worker.allocationSkills,
  });
  const [selectedSkillIds, setSelectedSkillIds] = useState<Set<number>>(new Set(userSkillIds));
  const [localShiftStart, setLocalShiftStart] = useState(formatShiftTime(worker.shiftStart));
  const [localShiftEnd, setLocalShiftEnd] = useState(formatShiftTime(worker.shiftEnd));

  useEffect(() => {
    if (open) {
      setLocalCaps({ ...worker.allocationSkills });
      setSelectedSkillIds(new Set(userSkillIds));
      setLocalShiftStart(formatShiftTime(worker.shiftStart));
      setLocalShiftEnd(formatShiftTime(worker.shiftEnd));
    }
  }, [open, worker, userSkillIds]);

  const handleCapChange = useCallback((skillId: AllocationSkillId, value: number) => {
    setLocalCaps((prev) => ({ ...prev, [skillId]: value }));
  }, []);

  const handleSkillToggle = useCallback((skillId: number, checked: boolean) => {
    setSelectedSkillIds((prev) => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(skillId);
      } else {
        newSet.delete(skillId);
      }
      return newSet;
    });
  }, []);

  const hasCapsChanges = useMemo(() => {
    return SKILL_IDS.some((id) => (localCaps[id] ?? 0) !== (worker.allocationSkills[id] ?? 0));
  }, [localCaps, worker.allocationSkills]);

  const hasSkillsChanges = useMemo(() => {
    if (selectedSkillIds.size !== userSkillIds.length) return true;
    return userSkillIds.some((id) => !selectedSkillIds.has(id));
  }, [selectedSkillIds, userSkillIds]);

  const hasShiftChanges = useMemo(() => {
    return localShiftStart !== formatShiftTime(worker.shiftStart) || localShiftEnd !== formatShiftTime(worker.shiftEnd);
  }, [localShiftStart, localShiftEnd, worker.shiftStart, worker.shiftEnd]);

  const hasChanges = hasCapsChanges || hasSkillsChanges || hasShiftChanges;

  const handleSave = useCallback(async () => {
    const payload: EditAgentPayload = {};
    if (hasCapsChanges || hasShiftChanges) {
      payload.allocationSkills = localCaps;
    }
    if (hasShiftChanges) {
      payload.shiftStart = localShiftStart;
      payload.shiftEnd = localShiftEnd;
    }
    if (hasCapsChanges && !hasShiftChanges) {
      payload.allocationSkills = localCaps;
    }
    if (hasSkillsChanges) {
      payload.skillIds = Array.from(selectedSkillIds);
    }
    await onSave(worker.userId, payload);
  }, [
    hasCapsChanges,
    hasSkillsChanges,
    hasShiftChanges,
    localCaps,
    selectedSkillIds,
    localShiftStart,
    localShiftEnd,
    worker.userId,
    onSave,
  ]);

  const skillsByType = useMemo(() => {
    const grouped = availableSkills.reduce(
      (acc, skill) => {
        const typeName = ALLOCATION_SKILL_TYPES[skill.allocationSkillTypeId] ?? `Type ${skill.allocationSkillTypeId}`;
        if (!acc[typeName]) {
          acc[typeName] = [];
        }
        acc[typeName].push(skill);
        return acc;
      },
      {} as Record<string, AvailableSkill[]>
    );
    Object.values(grouped).forEach((skills) => skills.sort((a, b) => a.name.localeCompare(b.name)));
    return Object.fromEntries(Object.entries(grouped).sort((a, b) => a[0].localeCompare(b[0])));
  }, [availableSkills]);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && !isSaving && onClose()}>
      <DialogContent onOpenAutoFocus={(e) => e.preventDefault()} className="flex max-h-[80vh] flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>{worker.name}</DialogTitle>
          <DialogDescription className="sr-only">Edit agent allocation settings</DialogDescription>
          <div className="flex items-center gap-2 pt-1">
            <Badge className="h-8 gap-1.5 rounded-md bg-slate-50" variant="outline">
              <Building2 className="text-muted-foreground size-4!" />
              {worker.department}
            </Badge>
            <Badge
              className={cn(
                "h-8 gap-1.5 rounded-md bg-slate-50",
                worker.receivingLeads
                  ? "border-emerald-200 bg-emerald-50 text-emerald-600"
                  : "border-rose-200 bg-rose-50 text-rose-600"
              )}
              variant="outline"
            >
              <div className={cn("size-1.5 rounded-full", worker.receivingLeads ? "bg-emerald-600" : "bg-rose-600")} />
              {worker.receivingLeads ? "Active" : "Inactive"}
            </Badge>
            <div className="hover:bg-accent flex h-8 items-center gap-2 rounded-md border px-2 shadow-xs transition-colors">
              <Clock className="shrink-0" size={14} />
              <Input
                type="time"
                value={localShiftStart}
                onChange={(e) => setLocalShiftStart(e.target.value)}
                disabled={isSaving}
                className="h-fit cursor-text rounded-none border-none p-0 text-xs shadow-none focus-visible:ring-0"
              />
              -
              <Input
                type="time"
                value={localShiftEnd}
                onChange={(e) => setLocalShiftEnd(e.target.value)}
                disabled={isSaving}
                className="h-fit cursor-text rounded-none border-none p-0 text-xs shadow-none focus-visible:ring-0"
              />
            </div>
          </div>
        </DialogHeader>

        <Tabs defaultValue="caps" variant="underline" className="flex grow flex-col overflow-hidden">
          <TabsList>
            <TabsTrigger value="caps">Allocation Caps</TabsTrigger>
            <TabsTrigger value="skills">Skills &amp; Attributes</TabsTrigger>
          </TabsList>

          <TabsContent value="caps" className="overflow-auto py-4">
            <div className="grid grid-cols-2 gap-4">
              {SKILL_IDS.map((skillId) => (
                <div key={skillId} className="flex items-center justify-between rounded-xl border p-4 shadow-sm">
                  <Label htmlFor={`cap-${skillId}`} className="text-sm">
                    {ALLOCATION_SKILLS[skillId]}
                  </Label>
                  <div className="flex items-center gap-2 rounded-lg bg-slate-50 p-1">
                    <Button
                      className="text-muted-foreground text-xl"
                      variant="ghost"
                      size="icon"
                      disabled={isSaving || (localCaps[skillId] ?? 0) <= 0}
                      onClick={() => handleCapChange(skillId, Math.max(0, (localCaps[skillId] ?? 0) - 1))}
                    >
                      -
                    </Button>
                    <Input
                      id={`cap-${skillId}`}
                      type="number"
                      min={0}
                      value={localCaps[skillId] ?? 0}
                      onChange={(e) => handleCapChange(skillId, parseInt(e.target.value, 10) || 0)}
                      disabled={isSaving}
                      className="h-fit w-8 rounded-none border-none p-0 text-center text-lg! font-semibold shadow-none focus-visible:ring-0"
                    />
                    <Button
                      className="text-muted-foreground text-xl"
                      variant="ghost"
                      size="icon"
                      disabled={isSaving}
                      onClick={() => handleCapChange(skillId, (localCaps[skillId] ?? 0) + 1)}
                    >
                      +
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="skills" className="overflow-auto pb-4">
            <Accordion className="px-0.5" defaultValue={Object.keys(skillsByType)} type="multiple">
              {Object.entries(skillsByType).map(([typeName, skills]) => (
                <AccordionItem key={typeName} className="border-0" value={typeName}>
                  <AccordionTrigger>{typeName}</AccordionTrigger>
                  <AccordionContent>
                    <div className="grid grid-cols-3 gap-2">
                      {skills.map((skill) => (
                        <div
                          key={skill.allocationSkillId}
                          className={cn(
                            "hover:bg-accent flex cursor-pointer items-center gap-2 rounded-lg border px-4 py-3 transition-colors",
                            selectedSkillIds.has(skill.allocationSkillId) &&
                              "bg-primary text-primary-foreground hover:bg-primary/90"
                          )}
                          onClick={() =>
                            handleSkillToggle(skill.allocationSkillId, !selectedSkillIds.has(skill.allocationSkillId))
                          }
                        >
                          {selectedSkillIds.has(skill.allocationSkillId) && <Check size={16} />}
                          <span className="text-sm">{skill.name}</span>
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button onClick={onClose} variant="outline" disabled={isSaving}>
            Close
          </Button>
          <Button onClick={handleSave} disabled={isSaving || !hasChanges}>
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
